import dayjs, { type Dayjs } from 'dayjs';
import { applyRange, queryDateRangePicker } from '../../utils/date-range-picker';
import { syncStcSelectDisplay } from '../../utils/stc-select';

export type EventsCalendarFilterDraft = {
  activityFormat: string;
  activityType: string;
  dateRange: [Dayjs, Dayjs] | null;
};

export interface SelectOption {
  label: string;
  value: string;
}

export function getEventsCalendarScope(): ParentNode {
  return (
    document.querySelector<HTMLElement>('[data-barba-namespace="news-events-calendar"]') ??
    document
  );
}

export function getSelectOptions(selectId: string): SelectOption[] {
  const select = document.getElementById(selectId);

  if (!(select instanceof HTMLSelectElement)) {
    return [];
  }

  return [...select.options].map((option) => ({
    label: option.text.trim(),
    value: option.value,
  }));
}

export function readEventsCalendarFilterDraft(root: ParentNode): EventsCalendarFilterDraft {
  const activityFormat = root.querySelector<HTMLSelectElement>('#activity_format')?.value ?? '';
  const activityType = root.querySelector<HTMLSelectElement>('#activity_type')?.value ?? '';
  const range = queryDateRangePicker(root);
  let dateRange: [Dayjs, Dayjs] | null = null;

  if (range?.inputFrom.value && range.inputTo.value) {
    dateRange = [dayjs(range.inputFrom.value), dayjs(range.inputTo.value)];
  }

  return { activityFormat, activityType, dateRange };
}

export function applyEventsCalendarFilterDraft(
  root: ParentNode,
  draft: EventsCalendarFilterDraft,
  options: { silent?: boolean } = {},
): void {
  const formatSelect = root.querySelector<HTMLSelectElement>('#activity_format');
  const typeSelect = root.querySelector<HTMLSelectElement>('#activity_type');

  if (formatSelect) {
    formatSelect.value = draft.activityFormat;
    if (options.silent) {
      syncStcSelectDisplay(formatSelect);
    } else {
      formatSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  if (typeSelect) {
    typeSelect.value = draft.activityType;
    if (options.silent) {
      syncStcSelectDisplay(typeSelect);
    } else {
      typeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  const range = queryDateRangePicker(root);
  if (!range) return;

  const { inputFrom, inputTo, displayInput } = range;

  if (draft.dateRange) {
    inputFrom.value = draft.dateRange[0].format('YYYY-MM-DD');
    inputTo.value = draft.dateRange[1].format('YYYY-MM-DD');
  } else {
    inputFrom.value = '';
    inputTo.value = '';
  }

  applyRange(inputFrom, inputTo, displayInput);

  if (!options.silent) {
    range.picker.dispatchEvent(new CustomEvent('date-range-change', { bubbles: true }));
  }
}

export function hasActiveEventsCalendarDrawerFilters(root: ParentNode): boolean {
  const draft = readEventsCalendarFilterDraft(root);

  return Boolean(draft.activityFormat || draft.activityType || draft.dateRange);
}

export function draftsEqual(
  left: EventsCalendarFilterDraft,
  right: EventsCalendarFilterDraft,
): boolean {
  const formatEqual = left.activityFormat === right.activityFormat;
  const typeEqual = left.activityType === right.activityType;
  const leftRange = left.dateRange;
  const rightRange = right.dateRange;

  if (!leftRange && !rightRange) {
    return formatEqual && typeEqual;
  }

  if (!leftRange || !rightRange) {
    return false;
  }

  return (
    formatEqual &&
    typeEqual &&
    leftRange[0].isSame(rightRange[0], 'day') &&
    leftRange[1].isSame(rightRange[1], 'day')
  );
}
